import {
  CheckOutlined,
  CloseOutlined,
  SwapOutlined,
  TransactionOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useTheme } from '@material-ui/core';
import {
  approvalsTransactions,
  getByIdApprovalTransactions,
} from '@portal/actions/approveTransaction';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import { VITE_APPLICATION_NAME } from '@portal/config/env';
import { getRouteStackPath } from '@portal/config/routes';
import { TransactionApprovalType } from '@portal/enum/approvalsType';
import RolesType from '@portal/enum/rolesType';
import { getTransactionName } from '@portal/enum/transactions';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import { TransactionType } from '@portal/models/enumerators';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import { translate } from '@portal/services/i18n';
import { isCpf, maskHiddenCpfOrCnpj } from '@portal/services/masks';
import { moneyFormat } from '@portal/utils/format';
import { validationStatus } from '@portal/utils/transaction';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <div className="approved-detail__transaction-card__details__row">
    <p className="approved-detail__container__label-value">{label}</p>
    <p className="approved-detail__container__requester-value">
      {value ?? '_'}
    </p>
  </div>
);

const DetailCard = ({
  icon,
  title,
  rows,
}: {
  icon: React.ReactNode;
  title: string;
  rows: { label: string; value?: React.ReactNode }[];
}) => (
  <AdvancedCard testId={`detail-card-${title}`}>
    <div className="approved-detail__transaction-card__header-icon">
      {icon}
      <h3>{title}</h3>
    </div>
    {rows.map((row, idx) => (
      <DetailRow key={idx} label={row.label} value={row.value} />
    ))}
  </AdvancedCard>
);

const DetailApproved = () => {
  const { palette } = useTheme();
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const params: { id: string } = useParams();
  const [isPinModalVisible, setIsPinModalVisible] = useState(false);
  const [status, setStatus] = useState<TransactionApprovalType>();

  const {
    user: { roles, me },
    approved: { transactionDetail },
  } = useReduxState();

  const isPix =
    transactionDetail?.transactionRequest?.type === TransactionType.PIX;

  const favoredTaxForPix = useMemo(() => {
    if (
      isPix &&
      transactionDetail?.transactionRequest?.favored?.taxIdentifier
    ) {
      return maskHiddenCpfOrCnpj(
        transactionDetail.transactionRequest.favored.taxIdentifier
      );
    }
    return null;
  }, [isPix, transactionDetail?.transactionRequest?.favored?.taxIdentifier]);

  const favoredNameForPix =
    isPix && transactionDetail?.transactionRequest?.favored?.alias;
  const favoredBankAgencyForPix =
    isPix && transactionDetail?.transactionRequest?.favored?.bankAgency;

  useEffect(() => {
    if (me.accountId) {
      dispatch(getByIdApprovalTransactions(params.id));
    }
  }, [dispatch, me.accountId, params.id]);

  const bankName = useMemo(() => {
    if (transactionDetail?.transactionRequest?.type === TransactionType.P2P) {
      return VITE_APPLICATION_NAME;
    }

    const favored = transactionDetail?.transactionRequest?.favored;
    if (favored?.bankUserName) return favored.bankUserName;
    if (favored?.bankCode) return favored.bankCode;
    return '_';
  }, [
    transactionDetail?.transactionRequest?.favored,
    transactionDetail?.transactionRequest?.type,
  ]);

  const requesterRows = [
    {
      label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.REQUESTER'),
      value: transactionDetail?.transactionRequest?.account?.name,
    },
    {
      label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.REQUEST_IN'),
      value: transactionDetail?.createdAt
        ? DateTime.fromISO(transactionDetail.createdAt).toFormat(
            translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
          )
        : '_',
    },
  ];

  const destinyRows = [
    {
      label: translate(
        'PAGES.HOME.APPROVED.DETAILS.CONTAINER.TYPE_TRANSACTION'
      ),
      value: transactionDetail?.transactionRequest?.type
        ? getTransactionName(transactionDetail.transactionRequest.type)
        : '_',
    },
    bankName && {
      label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.INSTITUTION'),
      value: bankName,
    },
  ].filter(Boolean) as { label: string; value?: React.ReactNode }[];

  const favoredRows = [
    {
      label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.WHO_REQUEST'),
      value:
        transactionDetail?.transactionRequest?.favored?.alias ??
        favoredNameForPix ??
        '_',
    },
    {
      label: translate(
        `COMPONENTS.CONFIRMATION_DETAILS.TAX_IDENTIFIER.${
          isCpf(
            transactionDetail?.transactionRequest?.favored?.taxIdentifier ?? ''
          )
            ? 'PERSON'
            : 'COMPANY'
        }`
      ),
      value: transactionDetail?.transactionRequest?.favored?.taxIdentifier
        ? maskHiddenCpfOrCnpj(
            transactionDetail.transactionRequest.favored.taxIdentifier
          )
        : favoredTaxForPix || '-',
    },
    (transactionDetail?.transactionRequest?.favored?.bankAgency ||
      favoredBankAgencyForPix) && {
      label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.AGENCY'),
      value:
        transactionDetail?.transactionRequest?.favored?.bankAgency ||
        favoredBankAgencyForPix,
    },
    transactionDetail?.transactionRequest?.favored?.bankAccount &&
      transactionDetail?.transactionRequest?.favored?.bankAccountDigit && {
        label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.ACCOUNT'),
        value: `${transactionDetail.transactionRequest.favored.bankAccount}-${transactionDetail.transactionRequest.favored.bankAccountDigit}`,
      },
  ].filter(Boolean) as { label: string; value?: React.ReactNode }[];

  const handleConfirmation = async (pin: string) => {
    setIsPinModalVisible(false);
    const encryptedPIN = encrypt(pin);

    dispatch(
      approvalsTransactions(
        status === TransactionApprovalType.REPROVE
          ? TransactionApprovalType.REPROVE
          : TransactionApprovalType.APPROVE,
        params.id,
        transactionDetail?.transactionRequestId || '',
        encryptedPIN
      )
    );
  };

  const canApproveOrReprove = roles.some(
    (item) =>
      item === RolesType.TRANSACTION_APPROVER || item === RolesType.MY_ACCOUNT
  );

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            active: false,
            title: translate('PAGES.HOME.APPROVED.DETAILS.PAGE_TITLE'),
          },
        ]}
      />
      <div className="approved-detail">
        <AdvancedPanelContentHeader
          testId="approved-detail-header"
          title={translate('PAGES.HOME.APPROVED.DETAILS.PAGE_TITLE')}
          description={translate(
            'PAGES.HOME.APPROVED.DETAILS.PAGE_DESCRIPTION'
          )}
          goBackAction={() => navigate(getRouteStackPath('HOME', 'HOME_PAGE'))}
        />
        <div className="approved-detail__transaction-card">
          <div className="approved-detail__transaction-card__header">
            <h2>{translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.TITLE')}</h2>
            <p className="approved-detail__container__status-transaction">
              {transactionDetail?.status
                ? validationStatus(transactionDetail?.status)
                : '_'}
            </p>
          </div>
          <div className="approved-detail__transaction-card__amount">
            <p className="approved-detail__container__label-value">
              {translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.LABEL_VALUE')}
            </p>
            <p className="approved-detail__container__answer-value">
              {moneyFormat(transactionDetail?.transactionRequest?.amount) ??
                '_'}
            </p>
          </div>
          <div className="approved-detail__transaction-card__details">
            <DetailCard
              icon={<UserOutlined />}
              title={translate(
                'PAGES.HOME.APPROVED.DETAILS.CONTAINER.REQUESTER'
              )}
              rows={requesterRows}
            />
            <DetailCard
              icon={<SwapOutlined />}
              title={translate(
                'PAGES.HOME.APPROVED.DETAILS.CONTAINER.TRANSACTION'
              )}
              rows={destinyRows}
            />
            <DetailCard
              icon={<TransactionOutlined />}
              title={translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.DESTINY')}
              rows={favoredRows}
            />
          </div>
          <div className="approved-detail__transaction-card__actions">
            {!transactionDetail?.transactionApprovals?.some(
              (item) => item.userId === me.id
            ) && (
              <>
                <AdvancedButton
                  text={translate('PAGES.HOME.APPROVED.DETAILS.BUTTON_REPROVE')}
                  variant="contained"
                  customStyles={{ background: palette.error.main }}
                  disableElevation
                  hoverColor={palette.error.light}
                  onClick={() => {
                    setIsPinModalVisible(true);
                    setStatus(TransactionApprovalType.REPROVE);
                  }}
                  disabled={!canApproveOrReprove}
                  startIcon={<CloseOutlined />}
                />
                <AdvancedButton
                  text={translate('PAGES.HOME.APPROVED.DETAILS.BUTTON_APPROVE')}
                  variant="contained"
                  onClick={() => {
                    setIsPinModalVisible(true);
                    setStatus(TransactionApprovalType.APPROVE);
                  }}
                  disabled={!canApproveOrReprove}
                  startIcon={<CheckOutlined />}
                />
              </>
            )}
          </div>
        </div>
      </div>
      <ConfirmationModal
        visible={isPinModalVisible}
        setVisible={setIsPinModalVisible}
        handleConfirmation={handleConfirmation}
        text={translate('PAGES.HOME.TRANSFER.PIN_CONFIRMATION_TEXT')}
        title={translate('PAGES.HOME.TRANSFER.PIN_CONFIRMATION_TITLE')}
      />
    </>
  );
};

export default DetailApproved;
